import axios, { AxiosInstance, CreateAxiosDefaults } from 'axios';

import { requestInterceptors, responseInterceptors } from '@app/common/inerceptors';
import { environment } from '@app/environments/environment';

const SHARED_HTTP_AGENT_SYMBOL = Symbol.for('nocowanie.ssr.sharedHttpAgent');
const SHARED_HTTPS_AGENT_SYMBOL = Symbol.for('nocowanie.ssr.sharedHttpsAgent');

type GlobalStore = typeof globalThis & {
    [SHARED_HTTP_AGENT_SYMBOL]?: unknown;
    [SHARED_HTTPS_AGENT_SYMBOL]?: unknown;
};

const attachInterceptors = (instance: AxiosInstance): AxiosInstance => {
    instance.interceptors.request.use(requestInterceptors.onOpenTelemetryInterceptor);
    instance.interceptors.response.use(
        responseInterceptors.onResponseSuccess,
        responseInterceptors.onSentryResponseError,
    );

    return instance;
};

const isNodeRuntime = (): boolean =>
    typeof process !== 'undefined' && process.release?.name === 'node';

const getNodeBuiltinModule = <T>(name: string): T | null => {
    if (!isNodeRuntime()) {
        return null;
    }

    const getBuiltinModule = process.getBuiltinModule as
        | ((id: string) => T | undefined)
        | undefined;

    if (typeof getBuiltinModule === 'function') {
        return getBuiltinModule(name) ?? null;
    }

    const nodeRequire = loadNodeRequire();

    return nodeRequire ? ((nodeRequire(name) as T) ?? null) : null;
};

const loadNodeRequire = (): NodeRequire | null => {
    if (!isNodeRuntime()) {
        return null;
    }

    return eval('require') as NodeRequire;
};

const getKeepAliveAgents = (): Pick<CreateAxiosDefaults, 'httpAgent' | 'httpsAgent'> => {
    const http = getNodeBuiltinModule<typeof import('http')>('http');
    const https = getNodeBuiltinModule<typeof import('https')>('https');

    if (!http || !https) {
        return {};
    }

    const globalStore = globalThis as GlobalStore;

    if (!globalStore[SHARED_HTTP_AGENT_SYMBOL] || !globalStore[SHARED_HTTPS_AGENT_SYMBOL]) {
        globalStore[SHARED_HTTP_AGENT_SYMBOL] = new http.Agent({
            keepAlive: true,
            maxSockets: 128,
            maxFreeSockets: 32,
            scheduling: 'lifo',
        });
        globalStore[SHARED_HTTPS_AGENT_SYMBOL] = new https.Agent({
            keepAlive: true,
            maxSockets: 128,
            maxFreeSockets: 32,
            scheduling: 'lifo',
        });
    }

    return {
        httpAgent: globalStore[SHARED_HTTP_AGENT_SYMBOL] as CreateAxiosDefaults['httpAgent'],
        httpsAgent: globalStore[SHARED_HTTPS_AGENT_SYMBOL] as CreateAxiosDefaults['httpsAgent'],
    };
};

export const createAxiosInstance = (config?: CreateAxiosDefaults): AxiosInstance => {
    return attachInterceptors(
        axios.create({
            ...getKeepAliveAgents(),
            ...config,
            timeout: Number(environment.API_TIMEOUT ?? 3000),
        }),
    );
};
